import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { finalize, Observable, Subject } from 'rxjs';
import { FileService, IFileUploadManagerMini } from '../../service/file.service';
import { OpenFileHelperService } from '../../service/open-file-helper.service';
import { PhotoPreviewService } from '../../service/photo-preview-service';

@Component({
    selector: 'app-multi-file-upload-mini',
    templateUrl: './multi-file-upload-mini.component.html',
    styleUrls: ['./multi-file-upload-mini.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MultiFileUploadMiniComponent implements OnInit {
  constructor(
    private readonly _fileService: FileService,
    private readonly _openFileHelperService: OpenFileHelperService,
    private readonly _photoPreviewService: PhotoPreviewService
  ) {}

  @Input() maxFiles: number = 4;
  // Accept AbstractControl for template binding; used internally as a FormArray.
  private _fileControls: FormArray;
  @Input() set fileControls(value: AbstractControl) {
    this._fileControls = value as FormArray;
  }
  get fileControls(): FormArray {
    return this._fileControls;
  }
  @Input() fileService: IFileUploadManagerMini;
  @Input() isReadonly: boolean = false;
  @Input() filesVisible: boolean = true;
  @Input() mimes: string[] = [];
  @Input() maxFileSize: number = 10;
  @Input() parentEntityId: string;
  @Input() buttonText: string;

  fileApplySubject: Subject<void>;
  fileApplyObservable: Observable<void>;
  fileDeleteSubject: Subject<FormGroup>;
  fileDeleteObservable: Observable<FormGroup>;
  isDeleteButtonDisabled: boolean = false;

  // 1. Lifecycle Hooks
  ngOnInit() {
    this.fileApplySubject = new Subject<void>();
    this.fileApplyObservable = this.fileApplySubject.asObservable();
    this.fileDeleteSubject = new Subject<FormGroup>();
    this.fileDeleteObservable = this.fileDeleteSubject.asObservable();
  }

  // 2. Event Handlers
  onUploadFileClick() {
    this.fileApplySubject.next();
  }

  onDeleteFileClick(fileIndex: number) {
    const fileControl = this.fileControls.controls[fileIndex] as FormGroup;
    const fileControlId = fileControl.controls['id'].value;
    this.isDeleteButtonDisabled = true;
    if (fileControlId) {
      this.fileService
        .deleteFile(fileControlId)
        .pipe(
          finalize(() => {
            this.isDeleteButtonDisabled = false;
          })
        )
        .subscribe(_ => {
          this.fileDeleteSubject.next(fileControl);
        });
    }
  }

  // 3. File Utilities
  isPdf(mime: string) {
    return this._fileService.isPdf(mime);
  }

  isWebImage(mime: string) {
    return this._fileService.isWebImage(mime);
  }

  checkFilesLimit(): boolean {
    const files = this.fileControls.controls as FormGroup[];
    return files.length >= this.maxFiles;
  }

  verifyFilesLoaded(): boolean {
    const files = this.fileControls.controls as FormGroup[];
    return !!files.find(x => !x.controls['id'].value);
  }

  // 4. Formatting Helpers
  formatFileCaption(input: string): string {
    if (!input) {
      return '<no title>';
    }

    const maxSymbolsBeforeType = 10;
    const maxFormatLength = 4;
    const lastDotIndex = input.lastIndexOf('.');
    if (lastDotIndex === -1 || input.slice(lastDotIndex + 1).length > maxFormatLength) {
      // Case 2: No dot or invalid format (extension too long)
      return input.length > maxSymbolsBeforeType ? `${input.slice(0, maxSymbolsBeforeType)}...` : input;
    }

    // Case 3: Valid format with dot
    const firstPart = input.slice(0, lastDotIndex);
    const secondPart = input.slice(lastDotIndex + 1);
    const shortened = firstPart.length > maxSymbolsBeforeType ? `${firstPart.slice(0, maxSymbolsBeforeType)}...` : firstPart;

    return `${shortened}.${secondPart}`;
  }

  onFileClick(fileIndex: number) {
    const fileControl = this.fileControls.controls[fileIndex] as FormGroup;
    const mimeType = fileControl.controls['mimeType'].value;
    if (this.isPdf(mimeType)) this.onOpenPdf(fileControl);
    if (this.isWebImage(mimeType)) this.onShowPhoto(fileControl);
  }

  onShowPhoto(fileControl: FormGroup) {
    this._showPhotoHandler(fileControl);
    if (!fileControl.controls['data'].value) {
      this.fileService.downloadFile(fileControl.controls['id'].value).subscribe(file => {
        this._fillFileControls(fileControl, file, () => {
          this._showPhotoHandler(fileControl);
        });
      });
    }
  }

  onOpenPdf(fileControl: FormGroup) {
    if (!fileControl.controls['data'].value) {
      this.fileService.downloadFile(fileControl.controls['id'].value).subscribe(file => {
        this._fillFileControls(fileControl, file, () => {
          this._openPdfHandler(fileControl);
        });
      });
    } else {
      this._openPdfHandler(fileControl);
    }
  }

  onFileAddedHandler(file: File) {
    const newAttachment = this.fileControls.controls.find(x => (x as FormGroup).controls['data'].value == file) as FormGroup;
    this.fileService
      .uploadFile(newAttachment.value, ...(this.parentEntityId ? [this.parentEntityId] : []))
      .pipe(
        finalize(() => {
          this.fileControls.updateValueAndValidity();
        })
      )
      .subscribe(x => {
        newAttachment.controls['id'].setValue(x);
      });
  }

  private _showPhotoHandler = (formGroup: FormGroup) => {
    this._photoPreviewService.showPhotoPreview({
      src: formGroup.controls['src'].value,
      fileName: formGroup.controls['originalName']?.value ?? formGroup.controls['caption']?.value,
    });
  };

  private _openPdfHandler = (formGroup: FormGroup) => {
    this._openFileHelperService.openBlobInNewTab(formGroup.controls['data'].value, formGroup.controls['mimeType'].value);
  };

  private _fillFileControls(formGroup: FormGroup, file: File, callback?: () => void) {
    formGroup.controls['data'].setValue(file); // fill 'data'
    if (file) {
      const reader = new FileReader();
      reader.onload = _event => {
        formGroup.controls['src'].setValue(reader.result); // fill 'src'
        if (callback) callback();
      };
      reader.readAsDataURL(file);
    }
  }
}
